import React, { useState } from "react";
import { Play, Youtube } from "lucide-react";
import { videos } from "../mock";
import Reveal from "./Reveal";
import VideoModal from "./VideoModal";

const colorMap = {
  blue: "#187EE4",
  orange: "#FF6C10",
  red: "#FF1228",
  yellow: "#F1C91F",
};

const Videos = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="videos" className="bg-white pt-16 md:pt-24 pb-10 md:pb-12">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="font-display text-3xl md:text-5xl" style={{ color: "#187EE4" }}>
              {videos.sectionTitle}
            </h2>
            <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed font-medium">
              {videos.intro}
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {videos.items.map((v, i) => {
            const color = colorMap[v.color];
            return (
              <Reveal key={v.id} delay={i * 90}>
                <button
                  onClick={() => setActive(v)}
                  className="group relative w-full aspect-[3/4] rounded-3xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl shadow-md"
                  aria-label={`Play ${v.label} video`}
                >
                  {/* Muted background image */}
                  {v.fit === "contain" ? (
                    <>
                      <img
                        src={v.image}
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl"
                        style={{ filter: "saturate(0.6) brightness(0.7)" }}
                      />
                      <img
                        src={v.image}
                        alt={`Brave Me ${v.label}`}
                        className="absolute inset-0 w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                      />
                    </>
                  ) : (
                    <img
                      src={v.image}
                      alt={`Brave Me ${v.label}`}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      style={{ filter: "saturate(0.65) brightness(0.82)" }}
                    />
                  )}
                  {/* Color-tinted muted overlay */}
                  <div
                    className="absolute inset-0 transition-opacity duration-300 group-hover:opacity-70"
                    style={{
                      background: `linear-gradient(180deg, ${color}44 0%, ${color}22 40%, rgba(15,37,64,0.78) 100%)`,
                    }}
                  />

                  {v.badge && (
                    <span
                      className="absolute top-3 right-3 font-display text-[11px] px-3 py-1 rounded-full text-white shadow-md z-10"
                      style={{ backgroundColor: "#FF6C10" }}
                    >
                      {v.badge}
                    </span>
                  )}

                  {/* Prominent play button - the hero of the card */}
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span
                      className="flex items-center justify-center w-[72px] h-[72px] md:w-20 md:h-20 rounded-full shadow-xl ring-4 ring-white/40 transition-all duration-300 group-hover:scale-110 group-hover:ring-white/70"
                      style={{ backgroundColor: color }}
                    >
                      <Play className="w-9 h-9 md:w-10 md:h-10 text-white ml-1" fill="white" strokeWidth={0} />
                    </span>
                  </span>

                  {/* Label */}
                  <span className="absolute bottom-0 left-0 right-0 p-5 text-left z-10">
                    <span className="block font-display text-white text-2xl md:text-[26px] brave-shadow-text">
                      {v.label}
                    </span>
                    <span className="mt-1 inline-flex items-center gap-1.5 text-white/90 text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                      {v.comingSoon ? "Coming soon" : "Watch the video"}
                    </span>
                  </span>
                </button>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={200}>
          <div className="mt-10 text-center">
            <a
              href={videos.channelUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-display text-white px-6 py-3 rounded-full transition-transform hover:scale-105 shadow-md"
              style={{ backgroundColor: "#FF1228" }}
            >
              <Youtube className="w-5 h-5" />
              Watch on YouTube
            </a>
          </div>
        </Reveal>
      </div>

      <VideoModal video={active} onClose={() => setActive(null)} />
    </section>
  );
};

export default Videos;
